import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Layout,
  Skeleton,
  Space,
  Typography,
  Avatar,
  Divider,
  Button,
  Switch,
  Popconfirm,
} from 'antd';
import ParseHTML from 'html-react-parser';
import {
  ArrowLeftOutlined,
  LockOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import mixpanel from 'mixpanel-browser';
import { ArticleContext } from '../Context/ArticleContext';
import { ThemeContext } from '../Context/ThemeContext';
import Parser from '../Card/Parser';
import * as Config from '../../Config';
import { NotifContext } from '../Context/NotifContext';
import { showHeartWhereClicked } from '../../Assets/Animations/HeartButton';
import getFontSize from '../../Assets/Utilities/FontSize';
import HelmetSeo from '../../Assets/Utilities/HelmetSeo';

const { Content } = Layout;
const { Text, Paragraph } = Typography;

const ArticleFeed = () => {
  const history = useHistory();
  const {
    article,
    update,
    articlePrivacy,
    setArticlePrivacy,
    loading,
  } = useContext(ArticleContext);
  const { theme } = useContext(ThemeContext);
  const { show } = useContext(NotifContext);
  const pageUrl = window.location.origin + window.location.pathname;
  // share
  const showShareSection = !!navigator.share;
  const share = async () => {
    const shareData = {};
    shareData.title = `Here is an update from ${article.user.name.fullName} on the Interact`;
    shareData.text =
      article.update.description.length > 130
        ? `${article.update.description.substring(0, 130)}...`
        : article.update.description;
    shareData.url = `${Config.app}/updates/${article.update.uid}`;
    mixpanel.track('Share Button Clicked', shareData);
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        mixpanel.track('Success: Share Article', shareData);
        show('success', 'Share Notification', 'Shared Successfully');
      } catch (err) {
        console.log('err', err);
        mixpanel.track('Share unsuccessfully', shareData);
        show(
          'error',
          'Share Notification',
          'There was an error while sharing!'
        );
      }
    } else {
      console.log(`This device doesn't support share via browser`);
    }
  };

  const confirmDelete = () => {
    update('delete', {
      article: article.update.uid,
    });
  };

  const cancelDelete = () => {
    // do nothing
  };

  const articlePrivacyChanged = (checked) => {
    update('privacy', {
      article: article.update.uid,
      public: checked,
    });
    setArticlePrivacy(checked ? 'public' : 'private');
  };

  return (
    <div>
      {article.user && article.update.description && (
        <HelmetSeo
          title={`Read ${article.user.name.fullName}'s update | Interact`}
          description={`${article.update.description.substring(0, 60)}...`}
          url={`${Config.app}/updates/${article.update.uid}`}
          image={article.user.picture}
        />
      )}
      <Content
        style={{
          padding: '80px 1.5rem 1.5rem 1.5rem',
          background: theme.content.bg,
        }}
        className='article-feed'
        onDoubleClick={showHeartWhereClicked}
      >
        <Row>
          <Col xs={0} md={4} lg={6} />
          <Col xs={24} md={16} lg={12}>
            <Skeleton loading={loading}>
              {article && !loading && (
                <Space direction='vertical'>
                  <Row
                    align='middle'
                    justify='center'
                    onClick={
                      article.isPrivate
                        ? null
                        : () => {
                            history.push(`/${article.user.profile_uid}`);
                          }
                    }
                    style={{
                      cursor: article.isPrivate ? '' : 'pointer',
                    }}
                  >
                    {/* avatar */}
                    <Col xs={3} sm={2}>
                      <Avatar
                        icon={
                          <img
                            src={article.user.picture.replace(
                              's96-c',
                              's386-c',
                              true
                            )}
                            alt={article.user.name.fullName}
                          />
                        }
                      />
                    </Col>
                    {/* About User */}
                    <Col xs={21} sm={22}>
                      <Text
                        style={{
                          color: theme.drawer.text,
                          textAlign: 'left',
                        }}
                        strong
                      >
                        {article.user.name.fullName} <br />
                        <Text
                          type='secondary'
                          style={{
                            padding: 0,
                            fontSize: '12px',
                            color: theme.drawer.secondaryText,
                          }}
                        >
                          {article.isPrivate ? 'Restricted' : 'Public'} Account{' '}
                          {article.isPrivate ? <LockOutlined /> : null}
                        </Text>
                      </Text>
                    </Col>
                  </Row>
                  <Divider />
                  <Row>
                    {/* Text */}
                    <Col xs={24}>
                      <Paragraph>
                        {!article.isPrivate ||
                        article.isPersonal ||
                        article.update.privacy === 'public' ? (
                          <Text
                            style={{
                              color: `${theme.content.text}`,
                              fontSize: `${getFontSize(
                                article.update.description.length
                              )}px`,
                            }}
                          >
                            {ParseHTML(Parser(article.update.description))}
                          </Text>
                        ) : (
                          <Col
                            style={{
                              padding: '0.5rem',
                              border: `1px dashed ${
                                theme.label === 'Dark' ? '#3f3b3b' : '#dbd2d2'
                              }`,
                            }}
                          >
                            <Text
                              style={{
                                color: theme.content.text,
                              }}
                            >
                              {article.message}
                            </Text>
                          </Col>
                        )}
                      </Paragraph>
                    </Col>
                    {/* <Divider /> */}

                    {/* Time */}
                    {!article.isPrivate ||
                    article.isPersonal ||
                    article.update.privacy === 'public' ? (
                      <Col xs={24}>
                        <Text
                          style={{
                            color: theme.content.text,
                            fontSize: `14px`,
                          }}
                          type='secondary'
                        >
                          <div
                            className='fb-like'
                            style={{ display: 'flex' }}
                            data-href={pageUrl}
                            // data-width="100%"
                            data-layout='button_count'
                            data-action='like'
                            data-size='large'
                            data-share='false'
                          />
                          {/* &nbsp;&bull;&nbsp; */}
                          {`${article.update.views} ${
                            article.update.views === 1 ? 'view' : 'views'
                          }`}
                          {/* &nbsp;&bull;&nbsp;
                          45&nbsp;❤️ */}
                          &nbsp;&bull;&nbsp;
                          {new Date(
                            article.update.createdAt
                          ).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true,
                          })}{' '}
                          &nbsp;&bull;&nbsp;
                          {new Date(article.update.createdAt).toDateString()}
                        </Text>
                      </Col>
                    ) : null}

                    {/* Reactions */}

                    {/* Privacy, delete */}
                    {article.isPersonal ? (
                      <Row
                        align='middle'
                        justify='center'
                        style={{ width: '100%' }}
                      >
                        <Col xs={24}>
                          <Divider />
                        </Col>
                        {/* Article Privacy */}
                        <Col xs={18}>
                          <Text
                            style={{
                              color: theme.content.text,
                            }}
                          >
                            Update is{' '}
                            {articlePrivacy === 'public' ? 'Public' : 'Private'}{' '}
                          </Text>{' '}
                          &nbsp;
                          <Switch
                            onChange={articlePrivacyChanged}
                            loading={false}
                            disabled={!article.isPrivate}
                            checked={articlePrivacy === 'public'}
                            style={{
                              backgroundColor:
                                articlePrivacy === 'public' ? '' : 'grey',
                            }}
                          />
                        </Col>
                        {/* Article Delete */}
                        <Col xs={6} align='right'>
                          <Popconfirm
                            title='Are you sure delete this update?'
                            onConfirm={confirmDelete}
                            onCancel={cancelDelete}
                            okText='Delete'
                            cancelText='Cancel'
                          >
                            <Button
                              style={{
                                backgroundColor: theme.content.bg,
                              }}
                              danger
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        </Col>
                        <Col xs={24}>
                          <Divider />
                        </Col>
                      </Row>
                    ) : null}

                    {/* Back to home  */}
                    <Col xs={24} align='center' style={{ padding: '1rem' }}>
                      {/* go back button */}
                      <Button
                        title='Back to homepage'
                        type='primary'
                        size='large'
                        shape='round'
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                          history.push('/');
                        }}
                      >
                        Back to homepage
                      </Button>
                      &nbsp;
                      {/* share button */}
                      {(!article.isPrivate || article.isPersonal) &&
                      showShareSection ? (
                        <Button
                          title='Share this update'
                          type='primary'
                          size='large'
                          shape='round'
                          icon={<ShareAltOutlined />}
                          onClick={share}
                        />
                      ) : null}
                    </Col>
                    <Col xs={24}>
                      <div
                        className='fb-comments'
                        data-href={pageUrl}
                        data-numposts='10'
                        data-lazy='false'
                        data-order-by='reverse_time'
                        data-colorscheme={theme.label.toLowerCase()}
                        data-width='100%'
                      />
                    </Col>
                  </Row>
                </Space>
              )}
            </Skeleton>
          </Col>
          <Col xs={0} md={4} lg={6} />
        </Row>
      </Content>
    </div>
  );
};

export default ArticleFeed;
